import { semanticColors, spacing } from '@moderneinc/design-system-tokens';
import {
  Box,
  CircularProgress,
  IconButton,
  SvgIcon,
  Typography
} from '@mui/material';
import { type FunctionComponent, useContext, useEffect } from 'react';
import { ERROR_MESSAGES } from '../../constants/error-messages';
import {
  type AuthenticatedDownloadManifest,
  type AuthorizedDownloadManifest,
  type DownloadManifestIndexDb,
  type DownloadState,
  DownloadsContext,
  REMOVE_DOWNLOAD_DELAY
} from '../../contexts/downloads.context';
import {
  getDownloadTimerStart,
  isDownloadInProgress
} from '../../helpers/download-manifest.helper';
import { getDetailedErrorMessage } from '../../helpers/graphql-error.helper';
import { showNotification } from '../../helpers/notification.helper';
import { useAuthDownload } from '../../hooks/use-auth-download.hooks';
import { useResolveDownloadUrl } from '../../hooks/use-resolve-download-url.hooks';
import { ErrorIcon } from '../../icons/icons';
import {
  ModCancelIcon,
  ModCheckedOutlineIcon
} from '../../icons/moderne-icons';
import { FlexBox } from '../styled-components/layouts/layouts.styled';
import { DownloadTimer } from './download-timer.component';
import { DownloadableRow } from './downloadable-row.styled';

const isAuthzDownloadManifest = (
  manifest: AuthenticatedDownloadManifest | AuthorizedDownloadManifest
): manifest is AuthorizedDownloadManifest => {
  return (manifest as AuthorizedDownloadManifest).repository !== undefined;
};

export const Downloadable: FunctionComponent<{
  manifest: (AuthenticatedDownloadManifest | AuthorizedDownloadManifest) &
    DownloadManifestIndexDb;
}> = ({ manifest }) => {
  const authDownload = useAuthDownload();
  const resolveDownloadUrl = useResolveDownloadUrl();
  const { updateDownloadSource, removeDownload, updateDownloadState } =
    useContext(DownloadsContext);

  const dismissDownload = () => removeDownload(manifest.indexDbId);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only run once on mount
  useEffect(() => {
    /**
     * `_app`'s route-keyed error boundary tears the provider down on
     * navigation; the remounted one restores this row and polls again. Without
     * this, both chains poll, and reach `authDownload` and save the file twice.
     */
    const abort = new AbortController();

    const handleDownload = async () => {
      if (manifest.state === 'PENDING') {
        try {
          await updateDownloadState(manifest.indexDbId, 'DOWNLOADING');

          const resolvedSourcePath = await resolveDownloadUrl(
            manifest,
            (source) => updateDownloadSource(manifest.indexDbId, source),
            abort.signal
          );

          if (abort.signal.aborted) {
            return;
          }

          const downloaded = await authDownload({
            path: resolvedSourcePath,
            fileName: manifest.fileName,
            Repository: isAuthzDownloadManifest(manifest)
              ? manifest.repository
              : undefined,
            errorMessage: 'Error downloading file',
            emptyMessage: manifest.emptyMessage
          });

          if (!downloaded) {
            throw new Error(
              'Authorization required — finish signing in to the repository host, then download again'
            );
          }

          await updateDownloadState(manifest.indexDbId, 'SUCCESS');

          await showNotification(
            'Download complete',
            `File: ${manifest.fileName}`
          );

          setTimeout(
            () => removeDownload(manifest.indexDbId),
            REMOVE_DOWNLOAD_DELAY
          );
        } catch (e) {
          if (abort.signal.aborted) {
            return;
          }

          showNotification('Error with download', `File: ${manifest.fileName}`);
          await updateDownloadState(
            manifest.indexDbId,
            'FAILURE',
            getDetailedErrorMessage(e) || ERROR_MESSAGES.downloads.failed.title
          );
        }
      }
    };

    handleDownload();

    return () => abort.abort();
  }, []);

  return (
    <DownloadableItem
      filename={manifest.fileName}
      state={manifest.state}
      errorMessage={manifest.errorMessage}
      startTimestamp={getDownloadTimerStart(manifest)}
      clearDownload={dismissDownload}
    />
  );
};

export const DownloadStateIcon: FunctionComponent<{
  state: DownloadState;
}> = ({ state }) => {
  switch (state) {
    case 'SUCCESS':
      return (
        <SvgIcon
          component={ModCheckedOutlineIcon}
          inheritViewBox
          color="success"
          sx={{
            fontSize: (theme) => theme.typography.pxToRem(32.5)
          }}
        />
      );
    case 'PENDING':
    case 'DOWNLOADING':
      return (
        <CircularProgress
          size={24}
          sx={{ margin: `${spacing.spacing_1_2}px` }}
        />
      );
    case 'FAILURE':
    default:
      return (
        <SvgIcon
          component={ErrorIcon}
          inheritViewBox
          color="error"
          sx={{
            fontSize: (theme) => theme.typography.pxToRem(32.5)
          }}
        />
      );
  }
};

const DownloadableItem: FunctionComponent<{
  filename: string;
  state: DownloadState;
  startTimestamp: number | null;
  clearDownload?: () => void;
  errorMessage?: string;
}> = ({ filename, state, clearDownload, errorMessage, startTimestamp }) => {
  return (
    <DownloadableRow>
      <Box>
        <Typography
          variant="bodySm"
          sx={{
            textOverflow: 'ellipsis'
          }}
        >
          {filename}
        </Typography>
        {isDownloadInProgress(state) &&
          (startTimestamp === null ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Queued
            </Typography>
          ) : (
            <DownloadTimer startTimestamp={startTimestamp} />
          ))}
        {state === 'SUCCESS' && (
          <Typography variant="caption">Completed</Typography>
        )}
        {/* Older persisted rows restore without a message */}
        {state === 'FAILURE' && !errorMessage && (
          <Typography variant="caption">
            Interrupted — start the download again
          </Typography>
        )}
        {errorMessage && (
          <Typography
            variant="subtitle2"
            sx={{
              color: semanticColors.color.status.error.solid
            }}
          >
            {errorMessage}
          </Typography>
        )}
      </Box>
      <FlexBox>
        <IconButton
          onClick={clearDownload}
          size="small"
          disabled={state === 'SUCCESS'}
        >
          <SvgIcon
            component={ModCancelIcon}
            inheritViewBox
            color={state === 'SUCCESS' ? 'inherit' : 'secondary'}
            sx={{ fontSize: (theme) => theme.typography.pxToRem(32.5) }}
          />
        </IconButton>
        <IconButton size="small" sx={{ padding: 0 }}>
          <DownloadStateIcon state={state} />
        </IconButton>
      </FlexBox>
    </DownloadableRow>
  );
};
